/**
 * Composite Layer Strategy Implementation
 *
 * Implements layer-specific logic for Composite (C) layer processing.
 * Composite layer handles video files only and aggregates multiple layer assets.
 */

import {
  BaseLayerStrategy,
  LayerFieldConfig,
  ValidationResult,
} from './LayerStrategy';

export interface CompositeLayerMetadata {
  layerType: 'composite';

  // Core composite information
  compositeName?: string;
  compositeType?:
    | 'Music_Video'
    | 'Performance'
    | 'Scene'
    | 'Character'
    | 'Video_Remix';
  description?: string;

  // Video characteristics
  duration?: number; // seconds
  resolution?: string; // e.g., "1920x1080"
  aspectRatio?: string; // e.g., "16:9"
  frameRate?: number; // fps
  format?: string; // e.g., "mp4"

  // Component assets: exactly one per layer (Song, Star, Look, Move, World)
  componentAssets?: {
    songAsset?: string; // G layer asset (id or HFN)
    starAsset?: string; // S layer asset
    lookAsset?: string; // L layer asset
    moveAsset?: string; // M layer asset
    worldAsset?: string; // W layer asset
  };

  // Production metadata
  productionMethod?: string;
  creationDate?: Date;
  creator?: string;
  studio?: string;

  // AI-generated composite analysis
  sceneCount?: number;
  transitionStyle?: string[];
  visualEffects?: string[];
  colorPalette?: string[];
  dominantThemes?: string[];

  // Performance metrics
  viewCount?: number;
  likeCount?: number;
  shareCount?: number;
  engagementRate?: number;
}

export class CompositeLayerStrategy extends BaseLayerStrategy {
  readonly layerCode = 'C';
  readonly layerName = 'Composite';
  readonly layerDescription =
    'Aggregated multi-layer video assets combining multiple components';

  getRequiredFields(): string[] {
    return ['compositeName', 'compositeType'];
  }

  getOptionalFields(): string[] {
    return [
      'description',
      'duration',
      'resolution',
      'aspectRatio',
      'frameRate',
      'format',
      'componentAssets',
      'productionMethod',
      'creationDate',
      'creator',
      'studio',
      'sceneCount',
      'transitionStyle',
      'visualEffects',
      'colorPalette',
      'dominantThemes',
      'viewCount',
      'likeCount',
      'shareCount',
      'engagementRate',
    ];
  }

  getFieldConfig(): LayerFieldConfig {
    return {
      required: this.getRequiredFields(),
      optional: this.getOptionalFields(),
      computed: [
        'duration',
        'resolution',
        'aspectRatio',
        'frameRate',
        'format',
        'sceneCount',
      ],
      inherited: [],
    };
  }

  extractMetadataFromAI(aiResponse: any): CompositeLayerMetadata {
    const layerMetadata =
      aiResponse.layerMetadata || aiResponse.compositeMetadata || {};

    return {
      layerType: 'composite',

      // Core information
      compositeName:
        layerMetadata.compositeName || aiResponse.compositeName || '',
      compositeType: this.normalizeCompositeType(
        layerMetadata.compositeType || aiResponse.compositeType
      ),
      description: layerMetadata.description || aiResponse.description || '',

      // Video characteristics from analysis
      duration: layerMetadata.duration || aiResponse.duration,
      resolution: layerMetadata.resolution || aiResponse.resolution,
      aspectRatio: layerMetadata.aspectRatio || aiResponse.aspectRatio,
      frameRate:
        layerMetadata.frameRate || aiResponse.frameRate || aiResponse.fps,
      format: layerMetadata.format || aiResponse.format,

      // Component assets
      componentAssets: this.extractComponentAssets(
        layerMetadata.componentAssets || aiResponse.componentAssets
      ),

      // Production metadata
      productionMethod:
        layerMetadata.productionMethod || aiResponse.productionMethod,
      creationDate: layerMetadata.creationDate || aiResponse.creationDate,
      creator: layerMetadata.creator || aiResponse.creator,
      studio: layerMetadata.studio || aiResponse.studio,

      // AI-generated analysis
      sceneCount: layerMetadata.sceneCount || aiResponse.sceneCount,
      transitionStyle: this.normalizeArray(
        layerMetadata.transitionStyle || aiResponse.transitionStyle
      ),
      visualEffects: this.normalizeArray(
        layerMetadata.visualEffects || aiResponse.visualEffects
      ),
      colorPalette: this.normalizeArray(
        layerMetadata.colorPalette || aiResponse.colorPalette
      ),
      dominantThemes: this.normalizeArray(
        layerMetadata.dominantThemes || aiResponse.dominantThemes
      ),
    };
  }

  enhanceWithDefaults(
    metadata: CompositeLayerMetadata
  ): CompositeLayerMetadata {
    return {
      ...this.getDefaultMetadata(),
      ...metadata,
      layerType: 'composite',

      // Ensure required fields have defaults
      compositeName: metadata.compositeName || 'Untitled Composite',
      compositeType: metadata.compositeType || 'Video_Remix',

      // Normalize arrays
      transitionStyle: this.normalizeArray(metadata.transitionStyle),
      visualEffects: this.normalizeArray(metadata.visualEffects),
      colorPalette: this.normalizeArray(metadata.colorPalette),
      dominantThemes: this.normalizeArray(metadata.dominantThemes),

      // Default video specs
      aspectRatio: metadata.aspectRatio || '16:9',
      frameRate: metadata.frameRate || 30,
    };
  }

  generateTags(
    metadata: CompositeLayerMetadata,
    description?: string
  ): string[] {
    const tags = new Set<string>();

    // Basic layer tags
    tags.add('composite');
    tags.add('video');
    tags.add('multi-layer');

    // Type-specific tags
    if (metadata.compositeType) {
      tags.add(metadata.compositeType.toLowerCase().replace(/_/g, '-'));

      switch (metadata.compositeType) {
        case 'Music_Video':
          tags.add('music-video');
          tags.add('mv');
          tags.add('musical');
          break;
        case 'Performance':
          tags.add('performance');
          tags.add('dance');
          tags.add('choreography');
          break;
        case 'Scene':
          tags.add('scene');
          tags.add('cinematic');
          tags.add('narrative');
          break;
        case 'Character':
          tags.add('character');
          tags.add('avatar');
          tags.add('personality');
          break;
        case 'Video_Remix':
          tags.add('remix');
          tags.add('mashup');
          tags.add('creative');
          break;
      }
    }

    // Resolution tags
    if (metadata.resolution) {
      if (
        metadata.resolution.includes('3840') ||
        metadata.resolution.includes('2160')
      ) {
        tags.add('4k');
        tags.add('ultra-hd');
      } else if (
        metadata.resolution.includes('1920') ||
        metadata.resolution.includes('1080')
      ) {
        tags.add('full-hd');
        tags.add('1080p');
      } else if (
        metadata.resolution.includes('1280') ||
        metadata.resolution.includes('720')
      ) {
        tags.add('hd');
        tags.add('720p');
      }
    }

    // Aspect ratio tags
    if (metadata.aspectRatio) {
      tags.add(metadata.aspectRatio.replace(':', 'x'));
      if (metadata.aspectRatio === '9:16') {
        tags.add('vertical');
        tags.add('mobile');
        tags.add('tiktok-format');
      } else if (metadata.aspectRatio === '16:9') {
        tags.add('horizontal');
        tags.add('widescreen');
        tags.add('youtube-format');
      } else if (metadata.aspectRatio === '1:1') {
        tags.add('square');
        tags.add('instagram-format');
      }
    }

    // Visual effect tags
    if (metadata.visualEffects && metadata.visualEffects.length > 0) {
      metadata.visualEffects.forEach(effect => {
        tags.add(effect.toLowerCase().replace(/\s+/g, '-'));
      });
    }

    // Theme tags
    if (metadata.dominantThemes && metadata.dominantThemes.length > 0) {
      metadata.dominantThemes.forEach(theme => {
        tags.add(theme.toLowerCase());
        tags.add(`${theme.toLowerCase()}-themed`);
      });
    }

    // Component tags
    if (metadata.componentAssets) {
      if (metadata.componentAssets.songAsset) tags.add('with-music');
      if (metadata.componentAssets.starAsset) tags.add('with-stars');
      if (metadata.componentAssets.lookAsset) tags.add('styled');
      if (metadata.componentAssets.moveAsset)
        tags.add('choreographed');
      if (metadata.componentAssets.worldAsset) tags.add('scenic');
    }

    // Production tags
    if (metadata.productionMethod) {
      tags.add(metadata.productionMethod.toLowerCase());
    }

    return Array.from(tags).slice(0, 30);
  }

  categorizeTags(tags: string[]): {
    primary: string[];
    secondary: string[];
    descriptive: string[];
  } {
    const categories = {
      primary: [] as string[],
      secondary: [] as string[],
      descriptive: [] as string[],
    };

    tags.forEach(tag => {
      const lowerTag = tag.toLowerCase();

      // Primary tags - type and format
      if (
        lowerTag.includes('video') ||
        lowerTag.includes('composite') ||
        ['music-video', 'performance', 'scene', 'remix'].includes(lowerTag)
      ) {
        categories.primary.push(tag);
      }
      // Secondary tags - technical specs
      else if (
        lowerTag.includes('hd') ||
        lowerTag.includes('4k') ||
        lowerTag.includes('format') ||
        lowerTag.includes('resolution')
      ) {
        categories.secondary.push(tag);
      }
      // Descriptive tags - themes and effects
      else {
        categories.descriptive.push(tag);
      }
    });

    return categories;
  }

  validateBusinessRules(metadata: CompositeLayerMetadata): ValidationResult {
    const errors: any[] = [];
    const warnings: any[] = [];

    // Business rule: Composite name should be meaningful
    if (metadata.compositeName && metadata.compositeName.trim().length < 3) {
      warnings.push({
        field: 'compositeName',
        message: 'Composite name should be at least 3 characters long',
        suggestion: 'Provide a more descriptive composite name',
      });
    }

    // Business rule: Must have at least one component asset
    if (metadata.componentAssets) {
      const hasComponents =
        metadata.componentAssets.songAsset ||
        !!metadata.componentAssets.starAsset ||
        !!metadata.componentAssets.lookAsset ||
        !!metadata.componentAssets.moveAsset ||
        !!metadata.componentAssets.worldAsset;

      if (!hasComponents) {
        warnings.push({
          field: 'componentAssets',
          message: 'Composite should include at least one component asset',
          suggestion: 'Add component assets from other layers',
        });
      }
    }

    // Business rule: Duration should be reasonable for a video
    if (metadata.duration) {
      if (metadata.duration < 5) {
        warnings.push({
          field: 'duration',
          message: 'Video duration is very short (< 5 seconds)',
          suggestion: 'Verify the video duration',
        });
      } else if (metadata.duration > 3600) {
        warnings.push({
          field: 'duration',
          message: 'Video duration is very long (> 1 hour)',
          suggestion: 'Consider splitting into shorter segments',
        });
      }
    }

    // Business rule: Frame rate should be standard
    if (
      metadata.frameRate &&
      ![24, 25, 30, 48, 50, 60].includes(metadata.frameRate)
    ) {
      warnings.push({
        field: 'frameRate',
        message: `Non-standard frame rate: ${metadata.frameRate} fps`,
        suggestion: 'Common frame rates are 24, 30, or 60 fps',
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  getDefaultMetadata(): CompositeLayerMetadata {
    return {
      layerType: 'composite',
      compositeName: '',
      compositeType: 'Video_Remix',
      description: '',
      aspectRatio: '16:9',
      frameRate: 30,
      componentAssets: {},
      transitionStyle: [],
      visualEffects: [],
      colorPalette: [],
      dominantThemes: [],
    };
  }

  getInheritanceRules(): {
    [field: string]: 'always' | 'if_empty' | 'never' | 'merge';
  } {
    return {
      // Composites don't inherit - they aggregate
      compositeName: 'never',
      compositeType: 'never',
      componentAssets: 'never',

      // Could merge themes from components
      dominantThemes: 'merge',
      colorPalette: 'merge',
    };
  }

  private normalizeArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    return [];
  }

  private normalizeCompositeType(
    value: any
  ): CompositeLayerMetadata['compositeType'] {
    if (!value) return 'Video_Remix';

    const normalized = String(value).toLowerCase().replace(/[\s-]/g, '_');

    if (normalized.includes('music') || normalized.includes('mv'))
      return 'Music_Video';
    if (normalized.includes('performance') || normalized.includes('dance'))
      return 'Performance';
    if (normalized.includes('scene') || normalized.includes('cinematic'))
      return 'Scene';
    if (normalized.includes('character') || normalized.includes('avatar'))
      return 'Character';

    return 'Video_Remix';
  }

  private extractComponentAssets(
    assets: any
  ): CompositeLayerMetadata['componentAssets'] {
    if (!assets) return {};

    const toId = (v: any): string | undefined => {
      if (typeof v === 'string') return v;
      if (v && typeof v === 'object' && (v.id || v._id || v.friendlyName || v.name)) {
        return (v.friendlyName || v.name || v.id || v._id) as string;
      }
      return undefined;
    };
    const first = (arr: any): string | undefined => {
      const a = Array.isArray(arr) ? arr[0] : arr;
      return toId(a);
    };

    if (Array.isArray(assets) && assets.length >= 5) {
      return {
        songAsset: toId(assets[0]),
        starAsset: toId(assets[1]),
        lookAsset: toId(assets[2]),
        moveAsset: toId(assets[3]),
        worldAsset: toId(assets[4]),
      };
    }

    return {
      songAsset: toId(assets.songAsset ?? assets.song ?? assets.G) ?? undefined,
      starAsset: first(assets.starAsset ?? assets.star ?? assets.starAssets ?? assets.stars ?? assets.S) ?? undefined,
      lookAsset: first(assets.lookAsset ?? assets.look ?? assets.lookAssets ?? assets.looks ?? assets.L) ?? undefined,
      moveAsset: first(assets.moveAsset ?? assets.move ?? assets.moveAssets ?? assets.moves ?? assets.M) ?? undefined,
      worldAsset: first(assets.worldAsset ?? assets.world ?? assets.worldAssets ?? assets.worlds ?? assets.W) ?? undefined,
    };
  }
}

// Export singleton instance
export const compositeLayerStrategy = new CompositeLayerStrategy();
